import {
    buttonClasses,
    ThemeOptions,
    toggleButtonClasses,
    inputLabelClasses,
    outlinedInputClasses,
} from "@mui/material";

export const components: ThemeOptions["components"] = {
    MuiButtonBase: {
        styleOverrides: {
            root: {
                "&:focus": {
                    outline: "none",
                },
            },
        },
    },
    MuiButton: {
        defaultProps: {
            variant: "contained",
        },
        styleOverrides: {
            root: {
                borderRadius: "25px",
                textTransform: "none",
                minWidth: "160px",
                ["&." + buttonClasses.disabled]: {
                    opacity: 0.2,
                },
            },
        },
    },
    MuiLoadingButton: {
        defaultProps: {
            variant: "contained",
        },
        styleOverrides: {
            loadingIndicator: ({ theme }) => ({
                color: "#fff",
            }),
            root: {
                ["&." + buttonClasses.disabled]: {
                    opacity: 0.2,
                },
            },
        },
    },
    MuiTableContainer: {
        styleOverrides: {
            root: ({ theme }) => ({
                border: `1px solid ${theme.palette.grey["100"]}`,
                borderRadius: "14px",
                backgroundColor: "#1B1C1C",
                padding: `0 ${theme.spacing(2)} ${theme.spacing(2)}`,
            }),
        },
    },
    MuiTableBody: {
        styleOverrides: {
            root: ({ theme }) => ({
                "&::before": {
                    content: '""',
                    display: "block",
                    height: theme.spacing(2),
                },
            }),
        },
    },
    MuiTableRow: {
        styleOverrides: {
            root: ({ theme }) => ({
                "&:nth-child(odd)": {
                    backgroundColor: theme.palette.grey["400"],
                },
                "&:last-child": {
                    "& > td:last-child": {
                        borderBottomRightRadius: "14px",
                    },
                    "& > td:first-child": {
                        borderBottomLeftRadius: "14px",
                    },
                },
            }),
            head: ({ theme }) => ({
                backgroundColor: `inherit!important`,
                borderTopLeftRadius: "14px",
                borderTopRightRadius: "14px",
                "& > td:first-child": {
                    borderTopLeftRadius: "14px",
                },
                "& > td:last-child": {
                    borderTopRightRadius: "14px",
                },
                "&>th": {
                    borderBottom: `1px solid ${theme.palette.grey["100"]}`,
                },
            }),
        },
    },
    MuiTableCell: {
        styleOverrides: {
            root: {
                border: "none",
            },
        },
    },
    MuiCssBaseline: {
        styleOverrides: {
            body: {
                marginBottom: "60px",
            },
            legend: {
                width: "unset",
            },
        },
    },
    MuiCheckbox: {
        styleOverrides: {
            root: {
                color: "#fff",
            },
        },
    },
    MuiDialog: {
        styleOverrides: {
            paper: {},
        },
    },
    MuiPaper: {
        styleOverrides: {
            root: ({ theme }) => ({
                border: `1px solid ${theme.palette.grey["100"]}`,
            }),
            rounded: {
                borderRadius: "14px",
            },
        },
    },
    MuiIconButton: {
        styleOverrides: {
            root: ({ theme }) => ({
                color: theme.palette.grey["100"],
            }),
        },
    },
    MuiDivider: {
        styleOverrides: {
            root: ({ theme }) => ({
                borderColor: theme.palette.grey["100"],
            }),
        },
    },
    MuiTextField: {
        defaultProps: {
            variant: "outlined",
            size: "small",
        },
    },
    MuiOutlinedInput: {
        styleOverrides: {
            notchedOutline: ({ theme }) => ({
                borderColor: theme.palette.grey["100"],
            }),
            root: {
                ["&." +
                outlinedInputClasses.disabled +
                " ." +
                outlinedInputClasses.notchedOutline]: {
                    borderStyle: "dashed",
                },
            },
        },
    },
    MuiTab: {
        styleOverrides: {
            root: ({ theme }) => ({
                color: theme.palette.grey["100"],
                fontSize: "16px",
                textTransform: "none",
                minWidth: 'auto',
                minHeight: 50,
            }),
        },
    },
    MuiTabs: {
        styleOverrides: {
            indicator: {
                height: "2px",
            },
            root: {
                marginBottom: "1em",
            },
        },
    },
    MuiToggleButtonGroup: {
        defaultProps: {
            color: "primary",
        },
        styleOverrides: {
            grouped: {
                "&:first-of-type": {
                    borderRadius: "25px 0 0 25px",
                },
                "&:last-of-type": {
                    borderRadius: "0 25px 25px 0",
                },
            },
        },
    },
    MuiToggleButton: {
        styleOverrides: {
            root: ({ theme }) => ({
                backgroundColor: "#343a40",
                color: "white",
                textTransform: "none",
                ["&." + toggleButtonClasses.selected]: {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                },
                "&:only-of-type": {
                    borderRadius: "25px",
                    minWidth: "80px",
                },
            }),
        },
    },
    MuiFormLabel: {
        styleOverrides: {
            root: ({ theme }) => ({
                color: theme.palette.grey["50"],
            }),
        },
    },
    MuiInputLabel: {
        styleOverrides: {
            root: ({ theme }) => ({
                ["&." + inputLabelClasses.error]: {
                    color: theme.palette.grey["100"],
                },
                ["&." + inputLabelClasses.disabled]: {
                    color: theme.palette.grey["500"],
                },
            }),
        },
    },
    MuiToolbar: {
        defaultProps: {
            disableGutters: true,
        },
        styleOverrides: {
            root: {
                "&.Toolbar-toolbar": {
                    borderBottom: "none!important",
                },
            },
        },
    },
    MuiAppBar: {
        defaultProps: {
            color: "default",
        },
        styleOverrides: {
            colorDefault: {
                backgroundColor: "#181818",
            },
            root: {
                border: "none",
            },
        },
    },
    MuiDrawer: {
        styleOverrides: {
            paper: {
                border: "none",
            },
        },
    },
    MuiSelect: {
        styleOverrides: {
            icon: {
                color: "rgba(255, 255, 255, 0.56)",
            },
            filled: {
                backgroundColor: "rgba(255, 255, 255, 0.09)",
            },
        },
    },
    MuiAvatar: {
        styleOverrides: {
            colorDefault: {
                backgroundColor: "#BDBDBD",
            },
        },
    },
};
